import { useEffect, useState, useMemo, useCallback } from 'react';
import { useRequest } from 'src/hooks';

export const useTableRows = (tableData, tableSettings) => {
  const req = useRequest();

  const fetchSub = useCallback(
    async ({ q, ixSubType = 0 }) => {
      let cancel = () => {};
      const query = q || '';

      const res = await req.post(
        '/lib/sub',
        { ixSubType, q: query },
        c => (cancel = c)
      );

      if (res.success && res.data.sub_list.length === 1) {
        return res.data.sub_list[0].sub_title ?? '';
      }
      return null;
    },
    [req]
  );

  const [rowsWithSubs, setRowsWithSubs] = useState([]);

  const tableRows = useMemo(() => {
    if (!Array.isArray(tableData)) return [];

    const rowKeyFields = tableSettings.row_keys?.map(r => r.field) ?? [];

    const columnDefs = tableSettings.column_keys ?? [];

    const rowsMap = [];

    const hasAllRowKeys = obj =>
      rowKeyFields.every(
        k => obj[k] !== null && obj[k] !== undefined && obj[k] !== ''
      );
    for (const item of tableData) {
      let records = [];

      if (typeof item.value === 'string') {
        try {
          const parsed = JSON.parse(item.value.replace(/'/g, '"'));
          if (parsed?.items) {
            records = parsed.items;
          } else {
            records = [{ ...item, value: parsed }]; // wrap primitive/string as record
          }
        } catch {
          records = [{ ...item, value: item.value }]; // fallback for non-JSON string
        }
      } else if (item.value?.items) {
        records = item.value.items;
      } else {
        records = [item];
      }
      for (const rec of records) {
        const source = rec ?? item;

        // Skip if this record cannot identify a row
        if (!hasAllRowKeys(source)) continue;

        // 1. Find matching row by ALL row keys
        let row = rowsMap.find(r =>
          rowKeyFields.every(k => r[k] === source[k])
        );

        // 2. If not found, create new row
        if (!row) {
          row = {};
          rowKeyFields.forEach(k => {
            row[k] = source[k];
          });
          rowsMap.push(row);
        }

        // 3. Attach values (columns)
        columnDefs.forEach(col => {
          const mapField = col.field;
          const key = col.value ?? col.field;

          if (rec[mapField] === col.value || col.isColumnKey) {
            row[key] = rec.target ?? rec.value ?? '';
          }
        });
      }
    }

    return Object.values(rowsMap);
  }, [tableData, tableSettings]);

  // asynchronously populate "sub" columns while keeping row structure
  useEffect(() => {
    const fillSubColumns = async () => {
      const updatedRows = await Promise.all(
        tableRows.map(async row => {
          const newRow = { ...row };

          for (const col of tableSettings.column_keys ?? []) {
            if (col.cellType === 'sub') {
              const key = col.value ?? col.field;
              const rawId = newRow[key];
              if (rawId != null) {
                const subRes = await fetchSub({
                  ixSubType: col.ixSubType,
                  q: rawId
                });
                if (subRes) newRow[`${key}_sub`] = subRes ?? rawId;
              }
            }
          }
          for (const col of tableSettings.row_keys ?? []) {
            if (col.cellType === 'sub') {
              const key = col.value ?? col.field;
              const rawId = newRow[key];
              if (rawId != null) {
                const subRes = await fetchSub({
                  ixSubType: col.ixSubType,
                  q: rawId
                });
                if (subRes) newRow[`${key}_sub`] = subRes ?? rawId;
              }
            }
          }

          return newRow;
        })
      );

      setRowsWithSubs(prev => {
        if (prev.length === updatedRows.length) {
          let same = true;
          for (let i = 0; i < prev.length; i++) {
            if (JSON.stringify(prev[i]) !== JSON.stringify(updatedRows[i])) {
              same = false;
              break;
            }
          }
          if (same) return prev;
        }
        return updatedRows;
      });
    };

    fillSubColumns();
  }, [tableRows, tableSettings]);

  return rowsWithSubs;
};
